import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { keyframes, css } from "@emotion/react";
import TtgFeature from "../TtgFeature";

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const ThinkingContainer = styled.div`
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  .thinking-header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    ${(props) =>
      props.isThinking
        ? css`
            background: linear-gradient(
              90deg,
              #607d8b 0%,
              #ffffff 25%,
              #9e9e9e 50%,
              #757575 75%,
              #607d8b 100%
            );
            background-size: 300% 100%;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            animation: ${gradientAnimation} 3s linear infinite;
          `
        : css`
            color: ${props.theme.text.disabled};
          `}
  }

  .thinking-content {
    background-color: ${(props) =>
      props.theme.type === "light"
        ? props.theme.gray.gray100
        : props.theme.gray.gray700};
    color: ${(props) =>
      props.theme.type === "light"
        ? props.theme.text.secondary
        : props.theme.blueGray.blueGray200};
    padding: 8px 12px;
    margin-top: 4px;
    border-radius: 8px;
    font-size: 12px;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    max-height: ${(props) => (props.expanded ? "none" : "0")};
    overflow: hidden;
    transition: max-height 0.3s ease;
  }
`;

const ThinkingBlock = ({
  step,
  content,
  stepIndex,
  totalSteps,
  currentStepIndex,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [thinkingDuration, setThinkingDuration] = useState(null);
  const [isThinking, setIsThinking] = useState(false);

  // Determine if this specific thinking block should be actively thinking
  const shouldBeThinking = currentStepIndex === stepIndex;

  // Duration from step data (survives remounts) or from local state during transition
  const durationFromStep =
    step?.completedAt && step?.timestamp
      ? Math.max(
          1,
          Math.round(
            (new Date(step.completedAt) - new Date(step.timestamp)) / 1000
          )
        )
      : null;

  // Track when thinking starts and stops for this specific block
  useEffect(() => {
    if (shouldBeThinking && !isThinking) {
      // This block just started thinking
      setIsThinking(true);
      setStartTime(Date.now());
      setThinkingDuration(null);
    } else if (!shouldBeThinking && isThinking) {
      // This block just finished thinking
      setIsThinking(false);
      if (startTime) {
        const duration = Math.max(
          1,
          Math.round((Date.now() - startTime) / 1000)
        );
        setThinkingDuration(duration);
      }
    }
  }, [shouldBeThinking, isThinking, startTime, stepIndex]);

  if (!content) return null;

  // Extract content from <thinking></thinking> tags if present
  const thinkingContent =
    content.match(/<thinking>(.*?)<\/thinking>/s)?.[1] || content;

  // Determine the text to display (prefer step data for duration - survives remounts)
  const getThinkingText = () => {
    if (isThinking) {
      return "Thinking...";
    }
    const duration = durationFromStep ?? thinkingDuration;
    if (duration !== null) {
      return `Thought for ${duration} seconds`;
    }
    return "Thinking...";
  };

  return (
    <ThinkingContainer
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}
      isThinking={isThinking}
    >
      <div className="thinking-header">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
        </svg>
        {getThinkingText()}
      </div>
      <TtgFeature>
        {({ treeIcon }) =>
          expanded && (
            <div className="thinking-content">
              {treeIcon}
              {thinkingContent}
            </div>
          )
        }
      </TtgFeature>
    </ThinkingContainer>
  );
};

export default ThinkingBlock;
